import apiInstance from '@api/index';
import toastMessage from '@components/Toast';
import { createAsyncThunk } from '@reduxjs/toolkit';

import { RunDeliverableEntityTradesParam } from 'pages/RunDeliverable/components/RunDeliverables/types';

import {
  CompanyEntityListResponse,
  CompanyEntityTransactionsResponse,
  SearchTradesResponse,
} from './types';

export const companyEntityTransactions = createAsyncThunk<
  CompanyEntityTransactionsResponse,
  RunDeliverableEntityTradesParam,
  { rejectValue: string }
>(
  'deliverables/companyEntityTransactions',
  async (params, { rejectWithValue }) => {
    try {
      toastMessage.info('Loading Company Entity Transactions...');
      const response = await apiInstance.companyEntity.companyTradesWithInfo({
        comp_entity_id: params.comp_entity_id,
      });
      if (response && response.data.success === true) {
        toastMessage.success('Company Entity Transactions loaded successfully');
      }
      return response.data;
    } catch (error) {
      if (error instanceof Error) {
        toastMessage.error(
          'Failed to load company entity transactions. ' + error.message,
        );
        return rejectWithValue(error.message);
      }
      return rejectWithValue('Failed to load company entity transactions');
    }
  },
);

export const fetchSearchTrades = createAsyncThunk<
  SearchTradesResponse,
  string[],
  { rejectValue: string }
>(
  'companyEntity/fetchSearchTrades',
  async (entityIds, { rejectWithValue }) => {
    try {
      const response = await apiInstance.hedgeAccounting.getBulkTransactionTitles({
        company_entity_ids: entityIds,
        page: 1,
        page_size: 1000,
      });
      if (response.data?.success && response.data?.data) {
        return response.data;
      }
      return rejectWithValue('Failed to load search trades');
    } catch (error) {
      if (error instanceof Error) return rejectWithValue(error.message);
      return rejectWithValue('Failed to load search trades');
    }
  },
);

export const companyEntityList = createAsyncThunk<
  CompanyEntityListResponse,
  string | null,
  { rejectValue: string }
>('deliverables/companyEntityList', async (id, { rejectWithValue }) => {
  try {
    const response = await apiInstance.filters.fetchCompanyEntityList(id);
    return response.data;
  } catch (error) {
    if (error instanceof Error) {
      toastMessage.error(
        'Failed to load company entity list. ' + error.message,
      );
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Failed to load company entity list');
  }
});

export const initSearchData = createAsyncThunk<
  void,
  void,
  { rejectValue: string }
>(
  'companyEntity/initSearchData',
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const entityResponse = await apiInstance.filters.fetchCompanyEntityList(null);
      const entityIds: string[] = (entityResponse.data?.data ?? []).map(
        (e: { comp_entity_id: string }) => e.comp_entity_id,
      );
      if (entityIds.length > 0) {
        dispatch(fetchSearchTrades(entityIds));
      }
      dispatch({ type: 'deliverables/companyEntityList/fulfilled', payload: entityResponse.data });
    } catch (error) {
      if (error instanceof Error) return rejectWithValue(error.message);
      return rejectWithValue('Failed to init search data');
    }
  },
);
